import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import PayoutDetails from 'components/modules/accounting/payouts/payout-details';
import CargomaticModal from 'components/v2/modal';
import {
  getInvoicesForShipment,
  getPayoutsForShipment,
} from 'utils/AccountingWebAPIUtils';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import Tooltip from 'components/v2/Tooltip';
import AdjustmentTitle from './AdjustmentTitle';
import DateField from './DateField';
import { useTranslation } from 'react-i18next';
import { sluggify } from 'utils/localization/utils';
import { titleize } from 'underscore.string';
import formatMoney from 'utils/formatters/Money';

const RECEIVABLES = 'Receivables';

export default function ConsigneeCharges({
  shipmentId,
  isIntermodalShipmentType,
  charges,
  type,
  timezone,
  total,
}) {
  const { t } = useTranslation();
  const [lineItems, setLineItems] = useState([]);
  const [selectedLineItem, setSelectedLineItem] = useState();
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  function handleHideDetailsModal() {
    setShowDetailsModal(false);
  }

  async function getDocs() {
    try {
      if (type === RECEIVABLES) {
        const {
          entity: { invoices },
        } = await getInvoicesForShipment(shipmentId);
        return invoices;
      }
      const {
        entity: { payouts },
      } = await getPayoutsForShipment(shipmentId);
      return payouts;
    } catch (e) {
      console.log('error fetching payment documents', e);
    }
  }

  async function getInvoicesOrPayouts() {
    try {
      const payoutDocuments = await getDocs();
      let mappedLineItems;

      if (type === RECEIVABLES) {
        mappedLineItems = charges.map((lineItem) => {
          const invoice = payoutDocuments.find(
            (p) => p._id === lineItem.invoice_id,
          );
          if (!invoice) {
            return {
              ...lineItem,
              hasInvoice: false,
              invoice: null,
            };
          }

          const { _id, number, timestamp, status, email_sent } = invoice;
          return {
            ...lineItem,
            hasInvoice: true,
            invoice: {
              _id,
              number,
              timestamp,
              status,
              email_sent,
            },
          };
        });
      } else {
        mappedLineItems = charges.map((lineItem) => {
          const payout = payoutDocuments.find(
            (p) => p._id === lineItem.payout_id,
          );
          if (!payout) {
            return {
              ...lineItem,
              payout: null,
            };
          }

          const { _id, number, timestamp, status, paid_date } = payout;
          return {
            ...lineItem,
            payout: {
              _id,
              number,
              timestamp,
              status,
              paid_date,
            },
          };
        });
      }
      const sortedItems = mappedLineItems.sort(
        (a, b) => b.consignee.company_name - a.consignee.company_name,
      );
      setLineItems(sortedItems);
    } catch (e) {
      console.log('Error getting invoices', e);
    }
  }

  function handleShowDetails(_id) {
    return () => {
      setSelectedLineItem(_id);
      setShowDetailsModal(true);
    };
  }

  function renderLink(lineItem) {
    if (!lineItem.invoice && !lineItem.payout) {
      return null;
    }

    let _id;
    let number;

    if (type === RECEIVABLES) {
      _id = lineItem.invoice._id;
      number = lineItem.invoice.number;
    } else {
      _id = lineItem.payout._id;
      number = lineItem.payout.number;
    }

    return (
      (<a data-testid="a-9c801fe3" href="#" onClick={handleShowDetails(_id)}>
        {number}
      </a>)
    );
  }

  function getInvoiceOrPayoutDetails(selectedLineItem) {
    if (type === RECEIVABLES) {
      return <InvoiceDetails invoiceId={selectedLineItem} />;
    }
    return <PayoutDetails payoutId={selectedLineItem} />;
  }

  useEffect(() => {
    getInvoicesOrPayouts();
  }, [charges]);

  const paymentDocumentTitle = type === RECEIVABLES ? 'INVOICE' : 'PAYOUT';
  return (
    (<div data-testid="div-7692abcc">
      <div data-testid="div-161037a2" className="">
        <h3 data-testid="h3-2bea2b3e" className="my-0 mb-4 text-base">
          {t(sluggify(`CONSIGNEE_${type}`))}
        </h3>
        <div data-testid="div-48ee2b9f" className="table-wrapper overflow-x-auto">
          <table data-testid="table-6f0b5899" className="default-table table-fixed">
            <thead data-testid="thead-ddcd55f7">
              <tr data-testid="tr-a85622b3">
                <th data-testid="th-899dcf11" className="w-48">{t('CHARGE')}</th>
                <th data-testid="th-97daa14e" className="w-20">{t('COST')}</th>
                <th data-testid="th-3cd3ad04" className="w-40">{t('ENTERED_DATE')}</th>
                <th data-testid="th-efc6bc33" className="w-52">
                  {t(sluggify(paymentDocumentTitle), { count: 1 })}
                </th>
                <th data-testid="th-2ccc602c" className="w-40">
                  {t(sluggify(`${paymentDocumentTitle}_DATE`))}
                </th>
                <th data-testid="th-c5f0da91" className="w-5">{t('SENT')}</th>
                <th data-testid="th-48153479" className="w-52">{titleize(t('CUSTOMER'))}</th>
              </tr>
            </thead>
            <tbody data-testid="tbody-42ae14b9">
              {lineItems.map((lineItem, index) => (<tr data-testid={`tr-34b1f4da-${index}`} key={lineItem._id}>
                <td data-testid={`td-1a9cf9b2-${index}`} className="break-words">
                  <AdjustmentTitle
                    lineItem={lineItem}
                    unapplied={
                      isIntermodalShipmentType && !lineItem.equipment
                    }
                  />
                </td>
                <td data-testid={`td-3c2795ab-${index}`}>{formatMoney(lineItem.value, lineItem?.currency)}</td>
                <td data-testid={`td-94b1d52a-${index}`}>
                  <DateField date={lineItem.timestamp} timezone={timezone} />
                </td>
                <td data-testid={`td-2e50eb29-${index}`}>{renderLink(lineItem)}</td>
                <td data-testid={`td-71819ac9-${index}`}>
                  <DateField
                    date={
                      lineItem.invoice?.timestamp ||
                      lineItem.payout?.timestamp
                    }
                    timezone={timezone}
                  />
                </td>
                <td data-testid={`td-5a5fe184-${index}`}>
                  {(lineItem.invoice?.email_sent ||
                    lineItem.payout?.email_sent) && (
                    <Tooltip
                      dataTestId={`Tooltip-43c40194-${index}`}
                      className="w-5 h-5"
                      body={() => (
                        <CircleCheckIcon className="text-green-cm h-5 w-5" />
                      )}>
                      <div data-testid={`div-30268a1c-${index}`}>Email Sent!</div>
                    </Tooltip>
                  )}
                </td>
                <td data-testid={`td-52eb9b4d-${index}`}>
                  <div
                    data-testid={`div-9148fe84-${index}`}
                    className="flex items-center space-x-2">
                    {lineItem.consignee?.company_name}
                  </div>
                </td>
              </tr>))}
              <tr data-testid="tr-e896b2dd">
                <td data-testid="td-e52d43e4">
                  <div data-testid="div-3aa5e17d" className="w-48">{t('TOTAL')}</div>
                </td>
                <td data-testid="td-bffe02ec" className="font-bold">
                  {formatMoney(total, lineItems[0]?.currency)}
                </td>
                <td data-testid="td-de6d66dd" colSpan={5}>&nbsp;</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      {showDetailsModal && (
        <CargomaticModal
          dataTestId="CargomaticModal-63be7080"
          title=""
          show={showDetailsModal}
          footer={null}
          body={getInvoiceOrPayoutDetails(selectedLineItem)}
          onHide={handleHideDetailsModal}
          dialogClassName="modal-override"
          location="top" />
      )}
    </div>)
  );
}

ConsigneeCharges.propTypes = {
  shipmentId: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  isIntermodalShipmentType: PropTypes.bool,
};

ConsigneeCharges.defaultProps = {
  timezone: 'America/Los_Angeles',
};
